<template>
  <div>
    <label
      id="add_document_button"
      :for="inputId"
      class="add-document-button"
      :class="{ 'opacity-50 pointer-events-none': isLoading }"
      style="display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;color:#374151;background:#fff;transition:background 0.15s;"
      @mouseenter="$event.currentTarget.style.background='#f9fafb'"
      @mouseleave="$event.currentTarget.style.background='#fff'"
    >
      <IconInnerShadowTop v-if="isLoading" width="16" class="animate-spin" style="color:#6b7280;" />
      <IconUpload v-else width="16" style="color:#6b7280;" />
      <span v-if="isLoading">{{ t('uploading_') }}</span>
      <span v-else class="whitespace-nowrap truncate">{{ t('add_document') }}</span>
      <span
        v-if="withGoogleDrive"
        class="dropdown dropdown-end dropdown-top inline"
        style="margin-left:auto;"
      >
        <label tabindex="0" class="flex items-center cursor-pointer">
          <IconChevronDown class="w-4 h-4 flex-shrink-0" />
        </label>
        <ul
          tabindex="0"
          :style="{ backgroundColor }"
          class="dropdown-content p-2 mt-2 shadow menu text-base mb-1 rounded-box text-right !text-base-content"
        >
          <li>
            <button type="button" @click="openGoogleDriveModal">
              <IconBrandGoogleDrive class="w-5 h-5 flex-shrink-0" />
              <span class="whitespace-nowrap text-sm normal-case font-medium">Google Drive</span>
            </button>
          </li>
        </ul>
      </span>
    </label>

    <button
      type="button"
      style="display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:8px 12px;border:1px solid #d1d5db;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;color:#374151;background:#fff;margin-top:6px;transition:background 0.15s;"
      @mouseenter="$event.currentTarget.style.background='#f9fafb'"
      @mouseleave="$event.currentTarget.style.background='#fff'"
      @click="openExistingTemplatesModal"
    >
      <IconLayoutList width="16" style="color:#6b7280;" />
      <span class="whitespace-nowrap">Add Existing Template</span>
    </button>

    <Teleport v-if="showExistingTemplatesModal" :to="modalContainerEl">
      <div class="modal modal-open items-start !animate-none overflow-y-auto">
        <div
          class="absolute top-0 bottom-0 right-0 left-0"
          @click.prevent="showExistingTemplatesModal = false"
        />
        <div class="modal-box pt-4 pb-6 px-6 mt-16 max-h-none w-full max-w-xl" style="position:relative;z-index:1">
          <div class="flex justify-between items-center border-b pb-2 mb-3 font-medium">
            <span class="text-lg font-semibold">Add Existing Template</span>
            <a href="#" class="text-xl" @click.prevent="showExistingTemplatesModal = false">&times;</a>
          </div>

          <p class="text-xs opacity-60 mb-3 leading-snug">
            The selected template's <strong>documents, fields, and submitter roles</strong>
            will be merged into the currently open template.
          </p>

          <input
            v-model="templateSearch"
            type="text"
            placeholder="Search templates…"
            class="input input-bordered w-full mb-3 text-sm"
            @input="filterTemplates"
          >

          <div v-if="isLoadingTemplates" class="flex items-center justify-center py-10">
            <IconInnerShadowTop class="animate-spin w-6 h-6 opacity-50" />
          </div>
          <div v-else-if="filteredTemplates.length === 0" class="text-center py-8 opacity-50 text-sm">
            No templates found.
          </div>
          <div v-else class="space-y-2 max-h-72 overflow-y-auto pr-1">
            <label
              v-for="tmpl in filteredTemplates"
              :key="tmpl.id"
              class="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 transition-colors"
              :class="{ 'border-purple-400 bg-purple-50': selectedExistingIds.includes(tmpl.id) }"
            >
              <input
                type="checkbox"
                class="checkbox checkbox-sm"
                :checked="selectedExistingIds.includes(tmpl.id)"
                @change="toggleExistingTemplate(tmpl.id)"
              >
              <div class="flex-1 min-w-0">
                <div class="font-medium text-sm truncate">{{ tmpl.name }}</div>
                <div class="text-xs opacity-50">
                  {{ tmpl.author ? tmpl.author + ' · ' : '' }}{{ tmpl.created_at ? new Date(tmpl.created_at).toLocaleString() : '' }}
                </div>
                <div class="text-xs opacity-40">
                  {{ tmpl.fields_count != null ? `${tmpl.fields_count} fields` : '' }}{{ tmpl.submitters_count != null ? ` · ${tmpl.submitters_count} roles` : '' }}
                </div>
              </div>
            </label>
          </div>

          <div v-if="mergeError" class="mt-3 text-red-600 text-sm text-center">
            {{ mergeError }}
          </div>

          <div class="flex justify-between items-center mt-4 pt-4 border-t">
            <span class="text-sm opacity-60">{{ selectedExistingIds.length }} selected</span>
            <div class="flex gap-2">
              <button type="button" class="btn btn-ghost btn-sm" @click="showExistingTemplatesModal = false">
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="selectedExistingIds.length === 0 || isMerging"
                @click="mergeIntoCurrentTemplate"
              >
                <IconInnerShadowTop v-if="isMerging" class="animate-spin w-4 h-4" />
                <span v-else>
                  Merge {{ selectedExistingIds.length > 0 ? selectedExistingIds.length : '' }}
                  Template{{ selectedExistingIds.length !== 1 ? 's' : '' }} Into This
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport v-if="showGoogleDriveModal" :to="modalContainerEl">
      <div class="modal modal-open items-start !animate-none overflow-y-auto">
        <div
          class="absolute top-0 bottom-0 right-0 left-0"
          @click.prevent="showGoogleDriveModal = false"
        />
        <div class="modal-box pt-4 pb-6 px-6 mt-20 max-h-none w-full max-w-xl">
          <div class="flex justify-between items-center border-b pb-2 mb-2 font-medium">
            <span class="modal-title">Google Drive</span>
            <a href="#" class="text-xl modal-close-button" @click.prevent="showGoogleDriveModal = false">&times;</a>
          </div>
          <div>
            <form v-if="showGoogleDriveOauthButton" method="post" :action="googleDriveOauthPath" @submit="isConnectGoogleDriveClicked = true">
              <input type="hidden" name="authenticity_token" :value="authenticityToken" autocomplete="off">
              <button
                id="gdrive_oauth_button"
                class="btn bg-white btn-outline w-full text-base font-medium mt-4"
                data-turbo="false"
                type="submit"
                :disabled="isConnectGoogleDriveClicked"
              >
                <span v-if="isConnectGoogleDriveClicked">
                  <span class="flex items-center justify-center space-x-2">
                    <IconInnerShadowTop class="animate-spin" /><span>Submitting...</span>
                  </span>
                </span>
                <span v-else>
                  <span class="flex items-center justify-center space-x-2">
                    <IconBrandGoogleDrive /><span>Connect Google Drive</span>
                  </span>
                </span>
              </button>
            </form>
            <div v-else class="relative">
              <iframe class="border border-base-300 rounded-lg" style="width:100%;height:440px;background:white;" src="/template_google_drive" />
              <div v-if="isLoadingGoogleDrive">
                <div class="bg-white absolute top-0 bottom-0 left-0 right-0 opacity-80 rounded-lg" style="margin:1px" />
                <div class="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
                  <IconInnerShadowTop class="animate-spin" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <form ref="form" class="hidden">
      <input v-for="file in googleDriveFiles" :key="file.id" name="google_drive_file_ids[]" :value="file.id">
      <input :id="inputId" ref="input" name="files[]" type="file" :accept="acceptFileTypes" multiple @change="upload">
    </form>
  </div>
</template>

<script>
import {
  IconUpload,
  IconInnerShadowTop,
  IconChevronDown,
  IconBrandGoogleDrive,
  IconLayoutList
} from '@tabler/icons-vue'

export default {
  name: 'DocumentsUpload',
  components: {
    IconUpload,
    IconInnerShadowTop,
    IconChevronDown,
    IconBrandGoogleDrive,
    IconLayoutList
  },
  inject: ['baseFetch', 't', 'backgroundColor'],
  props: {
    templateId: {
      type: [Number, String],
      required: true
    },
    authenticityToken: {
      type: String,
      required: false,
      default: ''
    },
    withGoogleDrive: {
      type: Boolean,
      required: false,
      default: false
    },
    acceptFileTypes: {
      type: String,
      required: false,
      default: 'image/*, application/pdf, application/zip'
    }
  },
  emits: ['success', 'error'],
  data () {
    return {
      isLoading: false,
      isConnectGoogleDriveClicked: false,
      isLoadingGoogleDrive: false,
      googleDriveFiles: [],
      showGoogleDriveModal: false,
      showGoogleDriveOauthButton: false,
      showExistingTemplatesModal: false,
      isLoadingTemplates: false,
      isMerging: false,
      mergeError: null,
      allTemplates: [],
      filteredTemplates: [],
      selectedExistingIds: [],
      templateSearch: ''
    }
  },
  computed: {
    inputId () {
      return 'el' + Math.random().toString(32).split('.')[1]
    },
    queryParams () {
      return new URLSearchParams(window.location.search)
    },
    googleDriveOauthPath () {
      const params = {
        access_type: 'offline',
        include_granted_scopes: 'true',
        prompt: 'consent',
        scope: [
          'https://www.googleapis.com/auth/userinfo.email',
          'https://www.googleapis.com/auth/drive.file'
        ].join(' '),
        state: new URLSearchParams({
          redir: `/templates/${this.templateId}/edit?google_drive_open=1`
        }).toString()
      }
      return `/auth/google_oauth2?${new URLSearchParams(params).toString()}`
    },
    modalContainerEl () {
      return this.$el.getRootNode().querySelector('#docuseal_modal_container')
    }
  },
  mounted () {
    window.addEventListener('message', this.messageHandler)
    if (this.queryParams.get('google_drive_open') === '1') {
      this.openGoogleDriveModal()
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  },
  beforeUnmount () {
    window.removeEventListener('message', this.messageHandler)
  },
  methods: {
    openGoogleDriveModal () {
      this.showGoogleDriveModal = true
      this.isLoadingGoogleDrive = true
    },
    messageHandler (event) {
      if (event.data.type === 'google-drive-files-picked') {
        this.googleDriveFiles = event.data.files || []
        this.$nextTick(() => {
          this.isLoadingGoogleDrive = true
          this.upload({ path: `/templates/${this.templateId}/google_drive_documents` }).then((resp) => {
            if (resp.ok) this.showGoogleDriveModal = false
          }).finally(() => {
            this.isLoadingGoogleDrive = false
          })
        })
      } else if (event.data.type === 'google-drive-picker-loaded') {
        this.isLoadingGoogleDrive = false
      } else if (event.data.type === 'google-drive-picker-request-oauth') {
        this.showGoogleDriveOauthButton = true
      }
    },
    async upload ({ path } = {}) {
      this.isLoading = true
      return this.baseFetch(path || `/templates/${this.templateId}/documents`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.$refs.form)
      }).then((resp) => {
        if (resp.ok) {
          resp.json().then((data) => {
            this.$emit('success', data)
            this.$refs.input.value = ''
            this.isLoading = false
          })
        } else if (resp.status === 422) {
          resp.json().then((data) => {
            if (data.status === 'pdf_encrypted') {
              const formData = new FormData(this.$refs.form)
              formData.append('password', prompt(this.t('enter_pdf_password')))
              this.baseFetch(this.uploadUrl, { method: 'POST', body: formData }).then(async (resp) => {
                if (resp.ok) {
                  this.$emit('success', await resp.json())
                  this.$refs.input.value = ''
                  this.isLoading = false
                } else {
                  alert(this.t('wrong_password'))
                  this.$emit('error', await resp.json().error)
                  this.isLoading = false
                }
              })
            } else if (data.status === 'google_drive_file_missing') {
              alert(data.error)
              this.$emit('error', data.error)
              this.isLoading = false
            } else {
              this.$emit('error', data.error)
              this.isLoading = false
            }
          })
        } else {
          resp.json().then((data) => {
            this.$emit('error', data.error)
            this.isLoading = false
          })
        }
        return resp
      }).catch(() => {
        this.isLoading = false
      })
    },

    async openExistingTemplatesModal () {
      this.showExistingTemplatesModal = true
      this.selectedExistingIds = []
      this.templateSearch = ''
      this.mergeError = null
      this.isLoadingTemplates = true

      try {
        const resp = await this.baseFetch('/templates/list_all', {
          headers: { 'Accept': 'application/json' }
        })
        if (resp.ok) {
          const list = await resp.json()
          this.allTemplates = (Array.isArray(list) ? list : []).filter(t => String(t.id) !== String(this.templateId))
          this.filteredTemplates = [...this.allTemplates]
        }
      } catch (err) {
      } finally {
        this.isLoadingTemplates = false
      }
    },

    filterTemplates () {
      const q = this.templateSearch.toLowerCase()
      this.filteredTemplates = this.allTemplates.filter(t =>
        t.name.toLowerCase().includes(q)
      )
    },

    toggleExistingTemplate (id) {
      const idx = this.selectedExistingIds.indexOf(id)
      if (idx === -1) {
        this.selectedExistingIds.push(id)
      } else {
        this.selectedExistingIds.splice(idx, 1)
      }
    },

    async mergeIntoCurrentTemplate () {
      if (this.selectedExistingIds.length === 0 || this.isMerging) return
      this.isMerging = true
      this.mergeError = null

      try {
        const csrfToken = document.querySelector('[name="csrf-token"]')?.content
        const resp = await this.baseFetch(`/templates/${this.templateId}/merge_templates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            template_ids: this.selectedExistingIds,
            merge_submitters: true
          })
        })

        if (resp.ok) {
          this.showExistingTemplatesModal = false
          this.selectedExistingIds = []
          if (window.Turbo) {
            window.Turbo.visit(window.location.href, { action: 'replace' })
          } else {
            window.location.reload()
          }
        } else {
          const data = await resp.json()
          this.mergeError = data.error || 'Failed to merge. Please try again.'
        }
      } catch (err) {
        this.mergeError = 'An unexpected error occurred. Please try again.'
      } finally {
        this.isMerging = false
      }
    }
  }
}
</script>
